import React, {useEffect, useState} from 'react';
import {Image, ScrollView, StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import CartButton from '../../components/CartButton';
import LoadingImg from '../../components/LoadingImg';
import InStoreShoppingModal from '../../components/modals/InStoreShoppingModal';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import WishlistButton from '../../components/WishlistButton';
import {useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import NoImage from '../../images/LMG_IMAGE_NOT_AVAILABLE_transparent.png';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import theme from '../../style/theme';
import apiService from '../../transport/apiService';
import {OrderLine} from '../../transport/apiServiceResponse';
import {toAUD} from '../../utils/functions';
import {makeABVText} from '../../utils/products';
import OrderLineItemStickers from '../cart/components/OrderLineItemStickers';

const RangeProductDetailScreen = observer(
  ({route, navigation}: RouteNavigatorScreenProps<AppRoute.PRODUCT>) => {
    const {product: productParam} = route.params || {};
    const [isLoadingProduct, setIsLoadingProduct] = useState(false);
    const [isProductNotFound, setIsProductNotFound] = useState(false);

    const {isWebDesktopScreen} = useWebScreenDimensions();

    // Deep link vs normal navigation handling. Not great
    // TODO: find a better way
    let product: OrderLine | null = null;
    if ((productParam as OrderLine)?.id) {
      product = productParam as OrderLine;
    }

    useEffect(() => {
      async function load() {
        if (typeof productParam === 'string') {
          setIsLoadingProduct(true);
          const fetchedProduct = await apiService.getProductById(productParam);
          if (!fetchedProduct) {
            setIsProductNotFound(true);
          }
          navigation.setParams({product: fetchedProduct});
          setIsLoadingProduct(false);
        }
      }
      load();
    }, [navigation, productParam]);

    const byline =
      product?.size?.quantity && product?.size?.quantity > 1
        ? `${product?.size?.quantity} x ${product?.size?.size}${product?.size?.unit}`
        : `${product?.size?.size}${product?.size?.unit}`;
    const originalPrice =
      product?.sell_price_cents !== product?.normal_price_cents ? (
        <View style={styles.priceLineRight}>
          <Text style={styles.was}>was </Text>
          <Text style={styles.originalPrice}>
            {toAUD(product?.normal_price_cents)}
          </Text>
        </View>
      ) : null;

    const abv = makeABVText(product);

    const regionDetails = () => {
      const country = product?.place_of_origin?.country ? (
        <View style={styles.originLine}>
          <Text style={styles.originBold}>Country: </Text>
          <Text style={styles.originNormal}>
            {product?.place_of_origin?.country}
          </Text>
        </View>
      ) : null;
      const region = product?.place_of_origin?.region ? (
        <View style={styles.originLine}>
          <Text style={styles.originBold}>Region: </Text>
          <Text style={styles.originNormal}>
            {product?.place_of_origin?.region}
          </Text>
        </View>
      ) : null;

      return (
        <View>
          {region}
          {country}
        </View>
      );
    };

    if (isProductNotFound) {
      return (
        <ContentView testID="shop-product-detail-screen">
          <Text>Product not found</Text>
        </ContentView>
      );
    }

    const renderProductImage = () => (
      <View
        style={styles.productImageHolder}
        testID="shop-product-detail-screen-product-image-holder">
        {product?.links.large_image ? (
          <Image
            source={{uri: product?.links.large_image}}
            style={styles.productImage}
            resizeMode="contain"
          />
        ) : null}
        {!product?.links.large_image && product?.links.image ? (
          <Image
            source={{uri: product?.links.image}}
            style={styles.productImage}
            resizeMode="contain"
          />
        ) : null}
        {!product.links.large_image && !product?.links.image && (
          <Image
            source={NoImage}
            style={styles.productImage}
            resizeMode="contain"
            width={325}
            height={325}
          />
        )}
        {product ? <OrderLineItemStickers item={product} size='large' isProductScreen/> : null}
      </View>
    );

    const renderProductDescription = () => (
      <View
        testID="shop-product-detail-screen-product-description"
        style={isWebDesktopScreen ? styles.productDescriptionMainDesktop : {}}>
        <Text style={styles.headline}>{product?.name}</Text>

        <View style={styles.priceLine}>
          <View style={styles.priceLineLeft}>
            <Text style={styles.price}>{toAUD(product?.sell_price_cents)}</Text>
            <Text style={styles.priceUnit}> each</Text>
          </View>
          {originalPrice}
        </View>

        <View style={styles.lineRow}>
          <Text style={styles.itemQty}>{byline}</Text>
          <Text style={styles.itemQty}>{abv}</Text>
        </View>
        {regionDetails()}
        <Text style={styles.copy}>{product?.description}</Text>
      </View>
    );

    if (isLoadingProduct || !product) {
      return (
        <ContentView testID="shop-product-detail-screen">
          <LoadingImg />
        </ContentView>
      );
    }

    // Images are a bit dicky as we're using this component for the range products
    // and the cart products. Cart products only have .image, while range has large.
    return (
      <ContentView testID="shop-product-detail-screen">
        <ScrollView
          testID="shop-product-detail-screen-scrollview"
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}>
          <View
            testID="shop-product-detail-screen-scrollview-content"
            style={isWebDesktopScreen ? styles.mainWebDesktop : {}}>
            {renderProductImage()}
            {renderProductDescription()}
          </View>
        </ScrollView>

        <View style={[styles.stickyFooter, {justifyContent: 'space-between'}]}>
          <View style={styles.priceLineFooter}>
            <Text style={styles.priceFooter}>
              {toAUD(product?.sell_price_cents)}
            </Text>
            <Text style={styles.priceUnitFooter}> ea</Text>
          </View>

          <View style={styles.footerButtonHolder}>
            <WishlistButton item={product} />
          </View>
          <View style={[styles.priceLineFooter, styles.priceLineFooterRight]}>
            <CartButton initSize={24} item={product} />
          </View>
        </View>
        <InStoreShoppingModal />
      </ContentView>
    );
  },
);

export default RangeProductDetailScreen;

const styles = StyleSheet.create({
  productDescriptionMainDesktop: {
    flex: 1,
  },
  mainWebDesktop: {
    flex: 1,
    flexDirection: 'row',
  },
  stickyFooter: {
    borderTopWidth: 1,
    borderTopColor: 'grey',
    height: 60,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
  },
  priceLineFooter: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flex: 1,
  },
  priceLineFooterRight: {
    justifyContent: 'flex-end',
  },
  footerButtonHolder: {
    flexDirection: 'row',
    justifyContent: 'center',
    flex: 1,
  },
  svg: {},
  priceFooter: {
    fontSize: 24,
    fontWeight: '500',
    color: theme.colour.text.heading.primary,
  },
  priceUnitFooter: {
    color: theme.colour.text.heading.primary,
  },
  productImageHolder: {
    alignItems: 'center',
    flex: 1,
    marginBottom: 5,
  },
  productImage: {
    width: '90%',
    aspectRatio: 1,
    overflow: 'visible',
    borderRadius: 4,
    // Do these need to be turned off?
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 5,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
  },
  priceLine: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
  },
  priceLineLeft: {
    flexDirection: 'row',
    alignItems: 'baseline',
    alignSelf: 'stretch',
  },
  price: {
    fontSize: 20,
    fontWeight: '500',
    color: 'grey',
  },
  priceUnit: {
    color: 'grey',
    fontWeight: 'bold',
    alignSelf: 'flex-end',
  },

  priceLineRight: {
    flexDirection: 'row',
    alignSelf: 'flex-end',
  },
  was: {
    color: 'grey',
  },
  originalPrice: {
    color: 'grey',
  },
  headline: {
    fontSize: 20,
    fontWeight: 'bold',
    marginVertical: 8,
    color: theme.colour.text.heading.primary,
  },
  itemQty: {
    color: 'grey',
    marginVertical: 8,
    fontSize: 12,
  },
  lineRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  copy: {
    fontSize: 16,
    marginTop: 12,
    marginBottom: 16,
    color: 'black',
  },
  originLine: {
    flexDirection: 'row',
    alignItems: 'flex-end',
  },
  originBold: {
    color: 'grey',
    fontWeight: 'bold',
    fontSize: 12,
  },
  originNormal: {
    color: 'grey',
    fontSize: 12,
  },
});
